<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"

import { Button, FormAlert, Skeleton } from "@/shared/ui"
import { hourLabels } from "../model/timeGrid"
import { captureStartMin } from "../model/startTimeCapture"
import { useRecordsStore } from "../model/store"
import MasterColumnHeader from "./MasterColumnHeader.vue"
import TimeGridBlock from "./TimeGridBlock.vue"
import TimeGridBooking from "./TimeGridBooking.vue"

const store = useRecordsStore()
const { t } = useI18n()

const isLoading = computed(
  () => store.dayStatus === "loading" || store.dayStatus === "idle",
)
const isError = computed(() => store.dayStatus === "error")
const isEmpty = computed(() => store.isDayEmpty)

const labels = computed(() => hourLabels(store.gridBounds))

function handleRetry(): void {
  void store.loadDay()
}

function onCreateTap(e: MouseEvent, masterId: string): void {
  store.openChooser(masterId, captureStartMin(e.offsetY ?? 0, store.gridBounds.startMin))
}
</script>

<template>
  <div
    class="time-grid"
    role="region"
    :aria-label="t('records.aria.gridLabel')"
    :aria-busy="isLoading ? 'true' : undefined"
  >
    <!-- Loading state -->
    <template v-if="isLoading">
      <div class="time-grid__skeleton">
        <Skeleton class="time-grid__skeleton-row" />
        <Skeleton class="time-grid__skeleton-row" />
        <Skeleton class="time-grid__skeleton-row" />
      </div>
    </template>

    <!-- Error state -->
    <template v-else-if="isError">
      <div class="time-grid__error">
        <FormAlert
          variant="destructive"
          :message="t('journal.loadError')"
        />
        <Button
          data-testid="retry-btn"
          variant="outline"
          size="sm"
          @click="handleRetry"
        >
          {{ t("journal.retry") }}
        </Button>
      </div>
    </template>

    <!-- Empty day state -->
    <template v-else-if="isEmpty">
      <div data-testid="empty-day" class="time-grid__empty">
        {{ t("records.dayEmpty") }}
      </div>
    </template>

    <!-- Grid -->
    <template v-else>
      <div class="time-grid__scroll">
        <!-- Sticky header row: time-axis corner + master headers -->
        <div class="time-grid__header-row">
          <div class="time-grid__corner" />
          <MasterColumnHeader
            v-for="master in store.workingMasters"
            :key="master.id"
            :master="master"
            :cat-slot="store.masterColorMap.get(master.id) ?? 0"
            :is-day-off="store.dayOffMasterIds.has(master.id)"
          />
        </div>

        <!-- Scrollable body: time axis + columns -->
        <div class="time-grid__body">
          <!-- Time axis -->
          <div data-testid="time-axis" class="time-grid__time-axis">
            <div
              v-for="label in labels"
              :key="label"
              class="time-grid__hour-label"
            >
              {{ label }}
            </div>
          </div>

          <!-- Master columns -->
          <div
            v-for="master in store.workingMasters"
            :key="master.id"
            class="time-grid__master-col"
          >
            <!-- Day-off column -->
            <div
              v-if="store.dayOffMasterIds.has(master.id)"
              :data-testid="`col-dayoff-${master.id}`"
              class="time-grid__dayoff"
            >
              {{ t("masters.schedule.dayOff") }}
            </div>

            <!-- Working column -->
            <div
              v-else
              :data-testid="`col-body-${master.id}`"
              class="time-grid__col-body"
            >
              <!-- tap-to-create background target (below booking cards) -->
              <button
                class="create-cell-target"
                :data-testid="`create-cell-target-${master.id}`"
                :aria-label="t('records.aria.createCell')"
                tabindex="-1"
                type="button"
                @click="onCreateTap($event, master.id)"
              />

              <!-- Hour grid lines -->
              <div
                v-for="label in labels"
                :key="label"
                class="time-grid__gridline"
              />

              <!-- Off-work overlay zones (after create-target, before cards) -->
              <div
                v-for="(zone, i) in (store.offWorkZonesByMaster.get(master.id) ?? [])"
                :key="i"
                class="offwork-zone"
                :data-testid="`offwork-zone-${master.id}-${i}`"
                :style="{ top: zone.topPx + 'px', height: zone.heightPx + 'px' }"
                aria-hidden="true"
              >
                <span class="offwork-zone__label">{{ t('records.offWork.label') }}</span>
              </div>

              <!-- Booking cards -->
              <TimeGridBooking
                v-for="booking in (store.bookingsByMaster.get(master.id) ?? [])"
                :key="booking.id"
                :booking="booking"
                :grid-start-min="store.gridBounds.startMin"
                :tz="store.salonTz"
                :cat-slot="store.masterColorMap.get(master.id) ?? 0"
                :service-name="store.serviceMap.get(booking.serviceId)"
                @click.stop
              />

              <!-- Block cards -->
              <TimeGridBlock
                v-for="block in (store.timeBlocksByMaster.get(master.id) ?? [])"
                :key="block.id"
                :block="block"
                :grid-start-min="store.gridBounds.startMin"
                :tz="store.salonTz"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* Feature-local grid metrics */
.time-grid {
  --time-col-w: 56px;
  --master-col-w: 140px;
  --hour-h: 60px;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.time-grid__skeleton {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}

.time-grid__skeleton-row {
  height: 80px;
  border-radius: 6px;
}

.time-grid__error {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  align-items: flex-start;
}

.time-grid__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  color: var(--muted-foreground);
  font-size: 0.875rem;
  text-align: center;
}

.time-grid__scroll {
  display: flex;
  flex-direction: column;
  overflow: auto;
  height: 100%;
}

.time-grid__header-row {
  display: flex;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--background);
  border-bottom: 1px solid var(--border);
}

.time-grid__corner {
  width: var(--time-col-w);
  flex-shrink: 0;
  border-right: 1px solid var(--border);
}

.time-grid__body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.time-grid__time-axis {
  position: sticky;
  left: 0;
  z-index: 5;
  background: var(--background);
  border-right: 1px solid var(--border);
  flex-shrink: 0;
  width: var(--time-col-w);
}

.time-grid__hour-label {
  height: var(--hour-h);
  display: flex;
  align-items: flex-start;
  padding: 4px 6px;
  font-size: 0.65rem;
  color: var(--muted-foreground);
}

.time-grid__master-col {
  flex-shrink: 0;
  width: var(--master-col-w);
  border-right: 1px solid var(--border);
  position: relative;
}

.time-grid__dayoff {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 120px;
  font-size: 0.75rem;
  color: var(--muted-foreground);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.time-grid__col-body {
  position: relative;
  height: 100%;
}

.create-cell-target {
  position: absolute;
  inset: 0;
  z-index: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 1px;
  }
}

.time-grid__gridline {
  height: var(--hour-h);
  border-bottom: 1px dashed var(--border);
}

.offwork-zone {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 1;
  background: color-mix(in oklab, var(--muted-foreground) 8%, transparent);
  display: flex;
  align-items: flex-start;
  padding: 4px 6px;
  overflow: hidden;
}

.offwork-zone__label {
  font-size: 0.75rem;
  color: var(--muted-foreground);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (min-width: 768px) {
  .time-grid {
    --master-col-w: 180px;
    --time-col-w: 64px;
  }
}
</style>
